import React, { useEffect, useState } from 'react'
import classNames from 'classnames'
import Translations from '@helpers/translations'
import PriceComparison from '@components/ui/PriceComparison'
import { ReactComponent as BinIcon } from '@svgs/keyline/bin.svg'
import trackError from '@helpers/trackError'
import CartItem, { Layout } from '../CartItem'
import styles from './styles.module.scss'
import { CartLineItem, Price } from 'types/checkout'
import { MAX_MTO_VARIANT_QUANTITY, MTO_FLAG } from '@consts/index'
import { QuantitySelector } from '../QuantitySelector'

export type Discount = {
  title: string
  discountedAmount: { amount: string }
}

type LineItemProps = {
  itemId: string
  disabled?: boolean
  image: string
  productTitle: string
  productType: string
  quantity: number
  productTags: CartLineItem['merchandise']['product']['tags']
  price: Price
  updateQuantity: (id: string, quantity: number) => void
  properties: {
    key: string
    value: string
  }[]
  url: string
  compareAtPrice: Price
  inventoryQuantity?: number
  discounts?: Discount[]
  layout?: Layout
}

function lineItemPrices({
  discounts = [],
  compareAtPrice,
  price,
  quantity
}: Pick<LineItemProps, 'discounts' | 'compareAtPrice' | 'price' | 'quantity'>) {
  try {
    const compareAtPriceAmount = Number(compareAtPrice?.amount)
    const priceAmount = Number(price?.amount)

    if (!priceAmount) {
      throw new Error('Line item price not available')
    }

    const discountCodePriceValue = discounts.reduce(
      (result, { discountedAmount }) =>
        result + Number(discountedAmount.amount),
      0
    )

    const isAutomaticDiscountApplied =
      discounts?.filter(
        ({ discountedAmount, title }) => !!discountedAmount?.amount && !!title
      ).length > 0

    const isPriceWithMarkdown =
      !!compareAtPriceAmount && compareAtPriceAmount !== priceAmount

    const discountedMarkdownPriceValue = isPriceWithMarkdown
      ? quantity * (compareAtPriceAmount - priceAmount)
      : 0

    const multibuyDiscountPrice = isAutomaticDiscountApplied
      ? discountCodePriceValue
      : null

    const discountCodePrice =
      discountCodePriceValue > 0 && !isAutomaticDiscountApplied
        ? discountedMarkdownPriceValue + discountCodePriceValue
        : null

    const priceWithDiscountApplied =
      multibuyDiscountPrice || discountCodePrice || discountedMarkdownPriceValue

    const fullPrice = !!compareAtPriceAmount
      ? compareAtPriceAmount * quantity
      : priceAmount * quantity

    return {
      fullPrice,
      originalPrice: fullPrice - priceWithDiscountApplied
    }
  } catch (error) {
    trackError(new Error('Failed to populate cart line item prices'), {
      error: error as Error
    })
    return {
      fullPrice: null,
      originalPrice: null
    }
  }
}

function LineItem({
  itemId,
  disabled,
  image,
  productTitle,
  productType,
  quantity,
  price,
  compareAtPrice,
  properties,
  productTags,
  inventoryQuantity = 0,
  updateQuantity,
  url,
  discounts = [],
  layout
}: LineItemProps) {
  const [error, setError] = useState(false)
  const discountTitles = discounts
    .filter(obj => 'title' in obj)
    .map(({ title }) => title)
  const { fullPrice, originalPrice } = lineItemPrices({
    discounts,
    quantity,
    price,
    compareAtPrice
  })
  const isMTO = properties.find(({ key }) => key === MTO_FLAG)?.value === 'true'
  const isDisabled = disabled && !isMTO

  if (isMTO) {
    inventoryQuantity = MAX_MTO_VARIANT_QUANTITY
  }

  const handleQuantityChange = async (newQty: number) => {
    await updateQuantity(itemId, newQty)
  }

  const handleRemove = () => handleQuantityChange(0)

  useEffect(() => {
    setError(quantity >= inventoryQuantity)
  }, [quantity, inventoryQuantity])

  const isSmallLayout = layout === 'small'
  const cartItemClassNames = classNames(styles.basket__item, {
    [styles.small]: isSmallLayout,
    'grid--vertical-center-desktop-up': !isSmallLayout
  })

  return (
    <div className={cartItemClassNames} data-testid="cart-item">
      <CartItem
        image={image}
        productTitle={productTitle}
        productType={productType}
        properties={properties}
        url={url}
        discounts={discountTitles}
        layout={layout}
        productTags={productTags}
      >
        <div
          className={`grid__item ${
            !isSmallLayout ? 'grid__item--20-desktop-up' : ''
          } ${styles.basket__itemButton} grid__item--nogrow`}
        >
          <button
            className={styles.lineItem__removeButton}
            type="button"
            onClick={handleRemove}
            disabled={isDisabled}
            aria-label={Translations.localise('common.cart.button-remove')}
          >
            <BinIcon width={16} height={16} />
          </button>
        </div>
        <div className={styles.lineItem__footer}>
          <QuantitySelector
            error={error}
            value={quantity}
            onChange={handleQuantityChange}
            min={1}
            max={inventoryQuantity}
            inventoryQuantity={inventoryQuantity}
            step={1}
            size={layout === 'small' ? 'small' : 'large'}
            disabled={isDisabled}
          />
          <div
            className={styles.lineItem__pricesWrapper}
            data-testid="item-price"
          >
            <PriceComparison
              classes={styles.lineItem__price}
              price={originalPrice}
              fullPrice={fullPrice}
              currency={price.currencyCode}
              size="medium-small-bold"
            />
          </div>
        </div>
      </CartItem>
      <div className={styles.basket__remove}>
        <button
          className={styles.lineItem__removeButton}
          type="button"
          onClick={handleRemove}
          disabled={isDisabled}
          aria-label={Translations.localise('common.cart.button-remove')}
        >
          <BinIcon width={16} height={16} />
        </button>
      </div>
    </div>
  )
}

export default LineItem
